import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import WelcomeModal from '../components/WelcomeModal';
import ShareModal from '../components/ShareModal';
import { 
  House, Users, Flame, Cross, Wallet, Trophy, 
  BookOpen, SignOut, CaretRight, Plus,
  Lightning, Target, HandFist, ChatCircle,
  X, PaperPlaneTilt, Play, Gear, Handshake,
  Heart, Star, CheckCircle, ArrowRight,
  ShareNetwork, Copy, TwitterLogo, FacebookLogo, LinkedinLogo, WhatsappLogo,
  Crown, DotsThree, BookBookmark, Sparkle, List, Compass,
  Image as ImageIcon, VideoCamera, Trash, Upload, Link as LinkIcon,
  ChartLineUp, Coins, Briefcase, Brain, Globe
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Section configurations with unique themes and heroes
const SECTION_CONFIG = {
  feed: {
    title: 'Feed',
    subtitle: 'Stay connected with your brothers',
    heroImage: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1200&auto=format&fit=crop&q=80',
    theme: 'dark',
    accentColor: '#C4A661',
    icon: House,
    quote: '"Iron sharpens iron, and one man sharpens another."',
    quoteSource: 'Proverbs 27:17'
  },
  wins: {
    title: 'Wins',
    subtitle: 'Celebrate victories with the brotherhood',
    heroImage: 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=1200&auto=format&fit=crop&q=80',
    theme: 'light',
    accentColor: '#C4A661',
    icon: Trophy,
    quote: '"The plans of the diligent lead surely to abundance."',
    quoteSource: 'Proverbs 21:5'
  },
  accountability: {
    title: 'Accountability',
    subtitle: 'Hold each other to the standard',
    heroImage: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=1200&auto=format&fit=crop&q=80',
    theme: 'dark',
    accentColor: '#E76F51',
    icon: Target,
    quote: '"As iron sharpens iron, so one person sharpens another."',
    quoteSource: 'Proverbs 27:17'
  },
  faith: {
    title: 'Faith',
    subtitle: 'Grow spiritually together',
    heroImage: 'https://images.unsplash.com/photo-1507692049790-de58290a4334?w=1200&auto=format&fit=crop&q=80',
    theme: 'light',
    accentColor: '#7C3AED',
    icon: Cross,
    quote: '"For God gave us a spirit not of fear but of power and love."',
    quoteSource: '2 Timothy 1:7'
  },
  business: {
    title: 'Business',
    subtitle: 'Build wealth and legacy',
    heroImage: 'https://images.unsplash.com/photo-1621972758399-da97a9f7e517?w=1200&auto=format&fit=crop&q=80',
    theme: 'dark',
    accentColor: '#10B981',
    icon: Wallet,
    quote: '"Whatever you do, work heartily, as for the Lord."',
    quoteSource: 'Colossians 3:23'
  },
  mastermind: {
    title: 'Mastermind',
    subtitle: 'Elite strategy sessions',
    heroImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&auto=format&fit=crop&q=80',
    theme: 'light',
    accentColor: '#C4A661',
    icon: Crown,
    quote: '"A wise man is full of strength."',
    quoteSource: 'Proverbs 24:5',
    premium: true
  },
  prayer: {
    title: 'Prayer Wall',
    subtitle: 'Lift each other up in prayer',
    heroImage: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1200&auto=format&fit=crop&q=80',
    theme: 'dark',
    accentColor: '#8B5CF6',
    icon: HandFist,
    quote: '"The prayer of a righteous person is powerful and effective."',
    quoteSource: 'James 5:16',
    premium: true
  }
};

// Daily scriptures for fallback
const DAILY_QUOTES = [
  { text: "Iron sharpens iron, and one man sharpens another.", source: "Proverbs 27:17" },
  { text: "Be watchful, stand firm in the faith, act like men, be strong.", source: "1 Corinthians 16:13" },
  { text: "For God gave us a spirit not of fear but of power and love and self-control.", source: "2 Timothy 1:7" },
  { text: "The plans of the diligent lead surely to abundance.", source: "Proverbs 21:5" },
  { text: "Commit your work to the Lord, and your plans will be established.", source: "Proverbs 16:3" },
  { text: "Whatever you do, work heartily, as for the Lord and not for men.", source: "Colossians 3:23" },
  { text: "A wise man is full of strength, and a man of knowledge enhances his might.", source: "Proverbs 24:5" },
];

const CommunityDashboard = () => {
  const { user, logout, isPaidMember, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const shareMenuRef = useRef(null);
  const [activeSection, setActiveSection] = useState('feed');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showDevotional, setShowDevotional] = useState(false);
  const [devotional, setDevotional] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Admin users have full Inner Circle access
  const isAdmin = user?.role === 'admin';
  const hasInnerCircleAccess = isPaidMember || isAdmin;
  
  // Welcome modal state - show for new users who haven't completed onboarding
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  
  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareData, setShareData] = useState({ url: '', text: '', title: '' });
  
  // Open share modal for a post
  const openShareModal = (post) => {
    const baseUrl = window.location.origin;
    setShareData({
      url: `${baseUrl}/community?post=${post.id}`,
      text: post.content?.substring(0, 200) + (post.content?.length > 200 ? '...' : ''),
      title: `Share ${post.author_name}'s post`
    });
    setShareModalOpen(true);
  };
  
  // Check if user needs onboarding
  useEffect(() => {
    if (user && user.onboarding_completed === false) {
      setShowWelcomeModal(true);
    }
  }, [user]);
  
  // Media upload state
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const fileInputRef = useRef(null);
  
  // Get current section config
  const currentSection = SECTION_CONFIG[activeSection] || SECTION_CONFIG.feed;
  const isLightTheme = currentSection.theme === 'light';
  
  // Fetch daily devotional
  useEffect(() => {
    const fetchDevotional = async () => {
      try {
        const response = await axios.get(`${API}/api/devotional/today`);
        setDevotional(response.data);
      } catch (error) {
      }
    };
    fetchDevotional();
  }, []);
  
  const dailyQuote = devotional ? {
    text: devotional.scripture,
    source: devotional.reference
  } : DAILY_QUOTES[new Date().getDay() % DAILY_QUOTES.length];

  // Share functions
  const getShareText = () => `"${dailyQuote.text}" — ${dailyQuote.source}\n\n🙏 Daily Word from POWR Brotherhood`;
  const handleCopyScripture = async () => {
    try {
      await navigator.clipboard.writeText(getShareText());
      toast.success('Scripture copied');
      setShowShareMenu(false);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };
  const handleShareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}`, '_blank');
    setShowShareMenu(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };
    if (showShareMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  // Navigation items
  const navItems = [
    { id: 'feed', label: 'Feed', icon: House },
    { id: 'wins', label: 'Wins', icon: Trophy },
    { id: 'accountability', label: 'Accountability', icon: Target },
    { id: 'faith', label: 'Faith', icon: Cross },
    { id: 'business', label: 'Business', icon: Wallet },
  ];

  const innerCircleItems = [
    { id: 'mastermind', label: 'Mastermind', icon: Crown, premium: true },
    { id: 'prayer', label: 'Prayer Wall', icon: HandFist, premium: true },
  ];

  const resourceItems = [
    { id: 'freedom-path', label: 'Freedom Path', icon: Lightning, link: '/freedom-path', highlight: true },
    { id: 'devotionals', label: 'Devotionals', icon: Compass, link: '/devotionals' },
    { id: 'training', label: 'Training', icon: BookOpen, link: '/courses' },
    { id: 'videos', label: 'Videos', icon: Play, link: '/videos' },
    { id: 'links', label: 'Links', icon: LinkIcon, link: '/links' },
    { id: 'resources', label: 'Resources', icon: Lightning, link: '/resources' },
  ];

  const investmentItems = [
    { id: '5-dollar-movement', label: '$5 Movement', icon: Coins, link: '/5-movement-platform' },
    { id: 'paper-portfolio', label: 'Paper Portfolio', icon: Briefcase, link: '/paper-portfolio-platform' },
    { id: 'wealth-intelligence', label: 'Wealth Intelligence', icon: Brain, link: '/wealth-intelligence' },
  ];

  // Fetch posts
  const fetchPosts = useCallback(async () => {
    try {
      const section = activeSection === 'feed' ? 'general' : activeSection;
      const response = await axios.get(`${API}/api/community/posts?section=${section}`, {
        headers: getAuthHeader()
      });
      setPosts(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [activeSection, getAuthHeader]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Handle media file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) {
        toast.error(`${file.name} is not a supported format`);
        return false;
      }
      if (isImage && file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return false;
      }
      if (isVideo && file.size > 100 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 100MB limit`);
        return false;
      }
      return true;
    });
    
    // Create preview URLs
    const newMedia = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : 'video'
    }));
    
    setSelectedMedia(prev => [...prev, ...newMedia].slice(0, 4)); // Max 4 media items
  };

  const removeSelectedMedia = (index) => {
    setSelectedMedia(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSubmitPost = async () => {
    if (!newPostContent.trim() && selectedMedia.length === 0) return;
    setSubmitting(true);
    
    try {
      const section = activeSection === 'feed' ? 'general' : activeSection;
      
      // First create the post
      const postRes = await axios.post(`${API}/api/community/posts`, 
        { content: newPostContent, section },
        { headers: getAuthHeader() }
      );
      
      const postId = postRes.data.id;
      
      // Then upload any media
      if (selectedMedia.length > 0) {
        setUploadingMedia(true);
        for (const media of selectedMedia) {
          const formData = new FormData();
          formData.append('file', media.file);
          
          await axios.post(
            `${API}/api/community/posts/${postId}/media`,
            formData,
            { 
              headers: { 
                ...getAuthHeader(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );
        }
        setUploadingMedia(false);
      }
      
      // Cleanup
      selectedMedia.forEach(m => URL.revokeObjectURL(m.preview));
      setSelectedMedia([]);
      setNewPostContent('');
      setShowNewPost(false);
      toast.success('Posted to the brotherhood');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to post');
    } finally {
      setSubmitting(false);
      setUploadingMedia(false);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      await axios.post(`${API}/api/community/posts/${postId}/like`, {}, { headers: getAuthHeader() });
      fetchPosts();
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  // Toggle comment section visibility
  const toggleComments = async (postId) => {
    const isExpanded = expandedComments[postId];
    
    if (!isExpanded) {
      // Fetch comments when expanding
      try {
        const response = await axios.get(`${API}/api/community/posts/${postId}/comments`, {
          headers: getAuthHeader()
        });
        setComments(prev => ({ ...prev, [postId]: response.data }));
      } catch (error) {
        console.error('Failed to fetch comments:', error);
        setComments(prev => ({ ...prev, [postId]: [] }));
      }
    }
    
    setExpandedComments(prev => ({ ...prev, [postId]: !isExpanded }));
  };

  // Submit a comment
  const handleSubmitComment = async (postId) => {
    const commentText = newComment[postId]?.trim();
    if (!commentText) return;
    
    try {
      await axios.post(
        `${API}/api/community/posts/${postId}/comments`,
        { content: commentText },
        { headers: getAuthHeader() }
      );
      
      // Clear the input
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      
      // Refresh comments
      const response = await axios.get(`${API}/api/community/posts/${postId}/comments`, {
        headers: getAuthHeader()
      });
      setComments(prev => ({ ...prev, [postId]: response.data }));
      
      // Update post comment count
      fetchPosts();
      
      toast.success('Comment posted!');
    } catch (error) {
      toast.error('Failed to post comment');
    }
  };

  // Theme classes based on section
  const bgMain = isLightTheme ? 'bg-[#F5F3EE]' : 'bg-[#0A0A0A]';
  const bgCard = isLightTheme ? 'bg-white' : 'bg-[#141414]';
  const borderColor = isLightTheme ? 'border-[#E5E2DB]' : 'border-[#262626]';
  const textPrimary = isLightTheme ? 'text-[#1A1A1A]' : 'text-[#F7F7F5]';
  const textSecondary = isLightTheme ? 'text-[#525252]' : 'text-[#A3A3A3]';
  const textMuted = isLightTheme ? 'text-[#737373]' : 'text-[#525252]';

  return (
    <div className={`min-h-screen ${bgMain} transition-colors duration-500`}>
      {/* Top Navigation - Always Dark */}
      <nav className="nav-glass border-b border-white/10 sticky top-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setShowMobileMenu(true)}
            className="lg:hidden p-2 -ml-2 text-[#A3A3A3] hover:text-[#F7F7F5]"
            data-testid="mobile-menu-btn"
          >
            <List size={24} />
          </button>
          
          <Link to="/hub" className="text-2xl font-extrabold tracking-tighter text-[#C4A661]">POWR</Link>
          
          <div className="flex items-center gap-4 md:gap-6">
            <Link to="/links" className="text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors hidden md:block">
              Links
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin-dashboard" className="text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors hidden md:block">
                Admin
              </Link>
            )}
            {isPaidMember && (
              <Link to="/inner-circle-hub" className="text-xs uppercase tracking-widest text-[#C4A661] hover:text-[#D4B97B] transition-colors hidden md:block">
                Inner Circle
              </Link>
            )}
            {!isPaidMember && (
              <Link to="/inner-circle-sales" className="hidden sm:block">
                <Button className="btn-gold h-9 px-4 text-xs">
                  Upgrade
                </Button>
              </Link>
            )}
            <Link to="/profile" className="w-9 h-9 rounded-full overflow-hidden bg-[#C4A661] flex items-center justify-center font-bold text-[#0A0A0A] hover:ring-2 hover:ring-[#C4A661] transition-all">
              {user?.profile_photo_path ? (
                <img 
                  src={`${API}/api/profile/photo/${user.profile_photo_path}?auth=${token}`}
                  alt={user?.name}
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: `${user?.photo_position_x ?? 50}% ${user?.photo_position_y ?? 50}%`
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <span className={user?.profile_photo_path ? 'hidden' : 'flex'}>
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </Link>
            <button onClick={handleLogout} className="text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors hidden md:block">
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 lg:hidden"
            onClick={() => setShowMobileMenu(false)}
          >
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'tween', duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="w-80 max-w-[85vw] h-full bg-[#0A0A0A] border-r border-[#262626] overflow-y-auto"
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-[#262626]">
                <span className="text-xl font-bold text-[#C4A661]">POWR</span>
                <button 
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 text-[#A3A3A3] hover:text-[#F7F7F5]"
                >
                  <X size={24} />
                </button>
              </div>

              {/* User Info */}
              <div className="p-4 border-b border-[#262626]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-[#C4A661] flex items-center justify-center font-bold text-[#0A0A0A] text-xl">
                    {user?.profile_photo_path ? (
                      <img 
                        src={`${API}/api/profile/photo/${user.profile_photo_path}?auth=${token}`}
                        alt={user?.name}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: `${user?.photo_position_x ?? 50}% ${user?.photo_position_y ?? 50}%`
                        }}
                      />
                    ) : (
                      <span>{user?.name?.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-[#F7F7F5]">{user?.name}</p>
                    <p className="text-sm text-[#C4A661]">
                      {hasInnerCircleAccess ? 'Inner Circle' : 'Free Member'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Navigation Links */}
              <div className="p-4 space-y-6">
                {/* Quick Links */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3">Quick Links</h3>
                  <div className="space-y-1">
                    {resourceItems.map((item) => (
                      <Link 
                        key={item.id}
                        to={item.link} 
                        onClick={() => setShowMobileMenu(false)}
                        className="flex items-center gap-3 px-4 py-3 text-[#F7F7F5] hover:bg-[#262626] transition-all"
                      >
                        <item.icon size={20} className="text-[#C4A661]" />
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Community Sections */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3">Community</h3>
                  <div className="space-y-1">
                    {navItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveSection(item.id);
                          setShowMobileMenu(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 transition-all text-left ${
                          activeSection === item.id 
                            ? 'bg-[#C4A661] text-[#0A0A0A]' 
                            : 'text-[#F7F7F5] hover:bg-[#262626]'
                        }`}
                      >
                        <item.icon size={20} weight={activeSection === item.id ? 'fill' : 'regular'} />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Inner Circle Section - Show to everyone */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Crown size={14} className="text-[#C4A661]" weight="fill" />
                    <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#C4A661]">Inner Circle</h3>
                    {!hasInnerCircleAccess && (
                      <span className="ml-auto text-[10px] px-2 py-0.5 bg-[#C4A661]/20 text-[#C4A661] rounded-full">Premium</span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {innerCircleItems.map((item) => (
                      hasInnerCircleAccess ? (
                        <button
                          key={item.id}
                          onClick={() => {
                            setActiveSection(item.id);
                            setShowMobileMenu(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 transition-all text-left ${
                            activeSection === item.id 
                              ? 'bg-[#C4A661]/20 text-[#C4A661]' 
                              : 'text-[#F7F7F5] hover:bg-[#262626]'
                          }`}
                        >
                          <item.icon size={20} weight={activeSection === item.id ? 'fill' : 'regular'} />
                          <span>{item.label}</span>
                        </button>
                      ) : (
                        <Link 
                          key={item.id}
                          to="/inner-circle-sales"
                          onClick={() => setShowMobileMenu(false)}
                          className="w-full flex items-center gap-3 px-4 py-3 transition-all text-left text-[#F7F7F5]/70 hover:bg-[#262626]"
                        >
                          <item.icon size={20} />
                          <span>{item.label}</span>
                          <Crown size={12} className="ml-auto text-[#C4A661]" weight="fill" />
                        </Link>
                      )
                    ))}
                    <Link 
                      to={hasInnerCircleAccess ? "/inner-circle-hub" : "/inner-circle-sales"}
                      onClick={() => setShowMobileMenu(false)}
                      className={`flex items-center gap-3 px-4 py-3 text-[#C4A661] hover:bg-[#262626] transition-all ${!hasInnerCircleAccess ? 'opacity-70' : ''}`}
                    >
                      <Sparkle size={20} />
                      <span>IC Dashboard</span>
                      {!hasInnerCircleAccess && <Crown size={12} className="ml-auto" weight="fill" />}
                    </Link>
                    <Link 
                      to={hasInnerCircleAccess ? "/business-suite" : "/inner-circle-sales"}
                      onClick={() => setShowMobileMenu(false)}
                      className={`flex items-center gap-3 px-4 py-3 text-[#C4A661] hover:bg-[#262626] transition-all ${!hasInnerCircleAccess ? 'opacity-70' : ''}`}
                      data-testid="mobile-nav-business-suite"
                    >
                      <Briefcase size={20} />
                      <span>Business Suite</span>
                      {!hasInnerCircleAccess && <Crown size={12} className="ml-auto" weight="fill" />}
                    </Link>
                    
                    {/* Investment Tools - Inside Inner Circle */}
                    <div className="mt-3 pt-3 border-t border-[#C4A661]/20">
                      <div className="flex items-center gap-2 mb-2 px-2">
                        <ChartLineUp size={12} className="text-[#10B981]" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#10B981]">Investment Tools</span>
                      </div>
                      {investmentItems.map((item) => (
                        hasInnerCircleAccess ? (
                          <Link 
                            key={item.id}
                            to={item.link} 
                            onClick={() => setShowMobileMenu(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-[#F7F7F5] hover:bg-[#262626] transition-all"
                            data-testid={`mobile-nav-${item.id}`}
                          >
                            <item.icon size={18} className="text-[#10B981]" />
                            <span className="text-sm">{item.label}</span>
                          </Link>
                        ) : (
                          <Link 
                            key={item.id}
                            to="/inner-circle-sales"
                            onClick={() => setShowMobileMenu(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-[#F7F7F5]/70 hover:bg-[#262626] transition-all"
                            data-testid={`mobile-nav-${item.id}`}
                          >
                            <item.icon size={18} className="text-[#10B981]" />
                            <span className="text-sm">{item.label}</span>
                            <Crown size={12} className="ml-auto text-[#C4A661]" weight="fill" />
                          </Link>
                        )
                      ))}
                    </div>

                    {/* Website Generator - Admin Only */}
                    {isAdmin && (
                      <div className="mt-3 pt-3 border-t border-[#C4A661]/20">
                        <div className="flex items-center gap-2 mb-2 px-2">
                          <Globe size={12} className="text-[#8B5CF6]" />
                          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B5CF6]">Admin Tools</span>
                        </div>
                        <Link 
                          to="/website-generator"
                          onClick={() => setShowMobileMenu(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-[#F7F7F5] hover:bg-[#262626] transition-all"
                          data-testid="mobile-nav-website-generator"
                        >
                          <Globe size={18} className="text-[#8B5CF6]" />
                          <span className="text-sm">Website Generator</span>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upgrade CTA */}
                {!hasInnerCircleAccess && (
                  <Link 
                    to="/inner-circle-sales" 
                    onClick={() => setShowMobileMenu(false)}
                    className="block bg-gradient-to-r from-[#C4A661] to-[#D4B671] p-4 text-center"
                  >
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Crown size={18} className="text-[#0A0A0A]" weight="fill" />
                      <span className="font-bold text-[#0A0A0A]">Upgrade to Inner Circle</span>
                    </div>
                    <p className="text-xs text-[#0A0A0A]/70">Take it to the next level</p>
                  </Link>
                )}

                {/* Account Links */}
                <div className="pt-4 border-t border-[#262626]">
                  <Link 
                    to="/profile" 
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center gap-3 px-4 py-3 text-[#F7F7F5] hover:bg-[#262626] transition-all"
                  >
                    <Gear size={20} className="text-[#A3A3A3]" />
                    <span>Settings</span>
                  </Link>
                  <button 
                    onClick={() => {
                      handleLogout();
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-[#262626] transition-all text-left"
                  >
                    <SignOut size={20} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dynamic Hero Section for Each Page */}
      <motion.section 
        key={activeSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={currentSection.heroImage}
            alt={currentSection.title}
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isLightTheme 
              ? 'bg-gradient-to-b from-white/80 via-white/60 to-[#F5F3EE]' 
              : 'bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]'
          }`} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Section Info */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${currentSection.accentColor}20` }}
                >
                  <currentSection.icon 
                    size={24} 
                    weight="fill"
                    style={{ color: currentSection.accentColor }}
                  />
                </div>
                {currentSection.premium && (
                  <span className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#C4A661] text-[#0A0A0A] rounded-full">
                    Inner Circle
                  </span>
                )}
              </div>
              
              <h1 className={`text-4xl md:text-5xl font-black tracking-tight mb-3 ${textPrimary}`}>
                {currentSection.title}
              </h1>
              <p className={`text-lg md:text-xl ${textSecondary}`}>
                {currentSection.subtitle}
              </p>
              
              {/* Section Quote */}
              <div className={`mt-6 p-4 rounded-xl ${isLightTheme ? 'bg-white/60 backdrop-blur' : 'bg-white/5 backdrop-blur'} border ${borderColor}`}>
                <p className={`font-serif italic text-lg ${textPrimary}`}>
                  {currentSection.quote}
                </p>
                <p className="text-sm mt-2" style={{ color: currentSection.accentColor }}>
                  — {currentSection.quoteSource}
                </p>
              </div>
            </motion.div>

            {/* Welcome Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="hidden lg:block"
            >
              <div className={`${bgCard} border ${borderColor} rounded-2xl p-6 relative overflow-hidden`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#C4A661]/10 to-transparent" />
                
                <div className="flex items-start gap-4 relative">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-gradient-to-br from-[#C4A661] to-[#8B7355] flex items-center justify-center ring-2 ring-[#C4A661]/30">
                    {user?.profile_photo_path ? (
                      <img 
                        src={`${API}/api/profile/photo/${user.profile_photo_path}?auth=${token}`}
                        alt={user?.name}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: `${user?.photo_position_x ?? 50}% ${user?.photo_position_y ?? 50}%`
                        }}
                      />
                    ) : (
                      <span className="text-xl font-bold text-[#0A0A0A]">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C4A661] mb-1">
                      Welcome back, Brother
                    </p>
                    <h2 className={`text-2xl font-black ${textPrimary}`}>
                      {user?.name?.split(' ')[0]}
                    </h2>
                    <p className={`text-sm ${textSecondary} mt-1`}>
                      Your brotherhood awaits
                    </p>
                  </div>
                </div>
                
                <div className={`flex items-center gap-4 mt-4 pt-4 border-t ${borderColor}`}>
                  <div className="text-center">
                    <p className={`text-lg font-bold ${textPrimary}`}>{hasInnerCircleAccess ? 'Inner Circle' : 'Free'}</p>
                    <p className={`text-xs ${textMuted} uppercase tracking-wider`}>Status</p>
                  </div>
                  <div className={`w-px h-8 ${isLightTheme ? 'bg-[#E5E2DB]' : 'bg-[#262626]'}`} />
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#C4A661]">
                      {new Date(user?.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </p>
                    <p className={`text-xs ${textMuted} uppercase tracking-wider`}>Member Since</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid lg:grid-cols-[240px_1fr_280px] gap-8">
          
          {/* Left Sidebar */}
          <aside className="hidden lg:block space-y-6">
            {/* Community Sections */}
            <div className={`${bgCard} border ${borderColor} p-4`}>
              <h3 className={`text-xs font-bold uppercase tracking-[0.15em] ${textSecondary} mb-4 px-2`}>Community</h3>
              <div className="space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-all text-left ${
                      activeSection === item.id 
                        ? 'bg-[#C4A661] text-[#0A0A0A]' 
                        : `${textSecondary} hover:${textPrimary} ${isLightTheme ? 'hover:bg-[#F5F3EE]' : 'hover:bg-[#262626]'}`
                    }`}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon size={18} weight={activeSection === item.id ? 'fill' : 'regular'} />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Resources */}
            <div className={`${bgCard} border ${borderColor} p-4`}>
              <h3 className={`text-xs font-bold uppercase tracking-[0.15em] ${textSecondary} mb-4 px-2`}>Resources</h3>
              <div className="space-y-1">
                {resourceItems.map((item) => (
                  <Link
                    key={item.id}
                    to={item.link}
                    className={`flex items-center gap-3 px-4 py-3 transition-all ${
                      item.highlight 
                        ? 'text-[#C4A661] hover:bg-[#C4A661]/10 font-semibold' 
                        : `${textSecondary} hover:${textPrimary} ${isLightTheme ? 'hover:bg-[#F5F3EE]' : 'hover:bg-[#262626]'}`
                    }`}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon size={18} weight={item.highlight ? 'fill' : 'regular'} />
                    <span className="text-sm font-medium">{item.label}</span>
                    {item.highlight && <span className="ml-auto text-[8px] px-1.5 py-0.5 bg-[#C4A661]/20 text-[#C4A661] uppercase tracking-wider font-bold">New</span>}
                  </Link>
                ))}
              </div>
            </div>

            {/* Inner Circle Exclusive Sections - Show to everyone, but redirect free users */}
            <div className={`bg-gradient-to-br ${isLightTheme ? 'from-[#F5F3EE] to-white' : 'from-[#1a1a1a] to-[#0A0A0A]'} border border-[#C4A661]/40 p-4`}>
              <div className="flex items-center gap-2 mb-4 px-2">
                <Crown size={14} className="text-[#C4A661]" weight="fill" />
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#C4A661]">Inner Circle</h3>
                {!hasInnerCircleAccess && (
                  <span className="ml-auto text-[10px] px-2 py-0.5 bg-[#C4A661]/20 text-[#C4A661] rounded-full">Premium</span>
                )}
              </div>
              <div className="space-y-1">
                {innerCircleItems.map((item) => (
                  hasInnerCircleAccess ? (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 transition-all ${
                        activeSection === item.id 
                          ? 'bg-[#C4A661]/20 text-[#C4A661] border-l-2 border-[#C4A661]' 
                          : `${textSecondary} hover:${textPrimary} ${isLightTheme ? 'hover:bg-[#F5F3EE]' : 'hover:bg-[#262626]'}`
                      }`}
                      data-testid={`nav-${item.id}`}
                    >
                      <item.icon size={18} weight={activeSection === item.id ? 'fill' : 'regular'} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  ) : (
                    <Link
                      key={item.id}
                      to="/inner-circle-sales"
                      className={`w-full flex items-center gap-3 px-4 py-3 transition-all ${textSecondary} hover:${textPrimary} ${isLightTheme ? 'hover:bg-[#F5F3EE]' : 'hover:bg-[#262626]'} opacity-70 hover:opacity-100`}
                      data-testid={`nav-${item.id}`}
                    >
                      <item.icon size={18} />
                      <span className="text-sm font-medium">{item.label}</span>
                      <Crown size={12} className="ml-auto text-[#C4A661]" weight="fill" />
                    </Link>
                  )
                ))}
                <Link
                  to={hasInnerCircleAccess ? "/inner-circle-hub" : "/inner-circle-sales"}
                  className={`flex items-center gap-3 px-4 py-3 text-[#C4A661] hover:text-[#D4B671] hover:bg-[#262626] transition-all ${!hasInnerCircleAccess ? 'opacity-70 hover:opacity-100' : ''}`}
                >
                  <Sparkle size={18} />
                  <span className="text-sm font-medium">IC Dashboard</span>
                  {!hasInnerCircleAccess && <Crown size={12} className="ml-auto" weight="fill" />}
                </Link>
                <Link
                  to={hasInnerCircleAccess ? "/business-suite" : "/inner-circle-sales"}
                  className={`flex items-center gap-3 px-4 py-3 text-[#C4A661] hover:text-[#D4B671] hover:bg-[#262626] transition-all ${!hasInnerCircleAccess ? 'opacity-70 hover:opacity-100' : ''}`}
                  data-testid="nav-business-suite"
                >
                  <Briefcase size={18} />
                  <span className="text-sm font-medium">Business Suite</span>
                  {!hasInnerCircleAccess && <Crown size={12} className="ml-auto" weight="fill" />}
                </Link>
                
                {/* Investment Tools - Inside Inner Circle */}
                <div className="mt-4 pt-4 border-t border-[#C4A661]/20">
                  <div className="flex items-center gap-2 mb-3 px-2">
                    <ChartLineUp size={12} className="text-[#10B981]" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#10B981]">Investment Tools</span>
                  </div>
                  {investmentItems.map((item) => (
                    hasInnerCircleAccess ? (
                      <Link
                        key={item.id}
                        to={item.link}
                        className={`flex items-center gap-3 px-4 py-2.5 ${textSecondary} hover:${textPrimary} ${isLightTheme ? 'hover:bg-[#F5F3EE]' : 'hover:bg-[#262626]'} transition-all`}
                        data-testid={`nav-${item.id}`}
                      >
                        <item.icon size={16} className="text-[#10B981]" />
                        <span className="text-sm font-medium">{item.label}</span>
                      </Link>
                    ) : (
                      <Link
                        key={item.id}
                        to="/inner-circle-sales"
                        className={`flex items-center gap-3 px-4 py-2.5 ${textSecondary} opacity-70 hover:opacity-100 ${isLightTheme ? 'hover:bg-[#F5F3EE]' : 'hover:bg-[#262626]'} transition-all`}
                        data-testid={`nav-${item.id}`}
                      >
                        <item.icon size={16} className="text-[#10B981]" />
                        <span className="text-sm font-medium">{item.label}</span>
                        <Crown size={12} className="ml-auto text-[#C4A661]" weight="fill" />
                      </Link>
                    )
                  ))}
                </div>

                {/* Website Generator - Admin Only */}
                {isAdmin && (
                  <div className="mt-4 pt-4 border-t border-[#C4A661]/20">
                    <div className="flex items-center gap-2 mb-3 px-2">
                      <Globe size={12} className="text-[#8B5CF6]" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B5CF6]">Admin Tools</span>
                    </div>
                    <Link
                      to="/website-generator"
                      className={`flex items-center gap-3 px-4 py-2.5 ${textSecondary} hover:${textPrimary} ${isLightTheme ? 'hover:bg-[#F5F3EE]' : 'hover:bg-[#262626]'} transition-all`}
                      data-testid="nav-website-generator"
                    >
                      <Globe size={16} className="text-[#8B5CF6]" />
                      <span className="text-sm font-medium">Website Generator</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Upgrade CTA - Only show for non-Inner Circle members */}
            {!hasInnerCircleAccess && (
              <div className={`bg-gradient-to-br ${isLightTheme ? 'from-[#F5F3EE] to-white' : 'from-[#141414] to-[#0A0A0A]'} border border-[#C4A661]/30 p-5`}>
                <div className="flex items-center gap-2 mb-3">
                  <Crown size={16} className="text-[#C4A661]" weight="fill" />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#C4A661]">Go Deeper</span>
                </div>
                <p className={`text-sm ${textSecondary} mb-2`}>Ready to take it to the next level?</p>
                <p className={`text-xs ${textMuted} mb-4`}>Premium training, mastermind access, and 1-on-1 accountability.</p>
                <Link to="/inner-circle-sales">
                  <Button className="w-full btn-gold text-sm h-10">
                    Explore Inner Circle
                  </Button>
                </Link>
              </div>
            )}
          </aside>

          {/* Main Feed */}
          <main>
            {/* Section Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${textPrimary} flex items-center gap-2`}>
                {innerCircleItems.find(n => n.id === activeSection) && (
                  <Crown size={20} className="text-[#C4A661]" weight="fill" />
                )}
                {navItems.find(n => n.id === activeSection)?.label || 
                 innerCircleItems.find(n => n.id === activeSection)?.label || 'Feed'}
              </h2>
              <Button 
                onClick={() => setShowNewPost(true)}
                className="btn-gold h-10 px-5"
                data-testid="new-post-btn"
              >
                <Plus size={16} className="mr-2" />
                Post
              </Button>
            </div>

            {/* Posts */}
            <div className="space-y-4">
              {loading ? (
                <div className={`text-center py-12 ${textSecondary}`}>Loading...</div>
              ) : posts.length === 0 ? (
                <div className={`text-center py-12 ${bgCard} border ${borderColor}`}>
                  <currentSection.icon size={40} className="text-[#C4A661] mx-auto mb-4" weight="duotone" />
                  <p className={textSecondary}>No posts yet in this section.</p>
                  <p className={`text-sm ${textMuted} mb-6`}>Be the first to share with the brotherhood.</p>
                  <Button onClick={() => setShowNewPost(true)} className="btn-gold">
                    Create Post
                  </Button>
                </div>
              ) : (
                posts.map((post) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`${bgCard} border ${borderColor} p-6 hover:border-[#C4A661]/30 transition-colors`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-[#C4A661] flex items-center justify-center font-bold text-[#0A0A0A] flex-shrink-0">
                        {post.author_photo_path ? (
                          <img 
                            src={`${API}/api/profile/photo/${post.author_photo_path}?auth=${token}`}
                            alt={post.author_name}
                            className="w-full h-full object-cover"
                            style={{
                              objectPosition: `${post.author_photo_position_x ?? 50}% ${post.author_photo_position_y ?? 50}%`
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : post.author_avatar ? (
                          <img 
                            src={post.author_avatar}
                            alt={post.author_name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <span className={post.author_photo_path || post.author_avatar ? 'hidden' : 'flex'}>
                          {post.author_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`font-bold ${textPrimary}`}>{post.author_name}</span>
                          {post.is_demo && (
                            <span className="px-1.5 py-0.5 bg-[#8B5CF6]/20 text-[#8B5CF6] text-[10px] font-bold rounded uppercase tracking-wider">
                              Demo Member
                            </span>
                          )}
                          {post.is_ai && (
                            <span className="px-1.5 py-0.5 bg-[#F59E0B]/20 text-[#F59E0B] text-[10px] font-bold rounded uppercase tracking-wider">
                              AI
                            </span>
                          )}
                          <span className={`text-xs ${textMuted}`}>
                            {new Date(post.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {post.is_demo && !post.is_ai && (
                          <div className="mb-2">
                            <span className="text-[10px] text-[#8B5CF6]/70 italic">Sample Discussion</span>
                          </div>
                        )}
                        <p className={`${textSecondary} whitespace-pre-wrap leading-relaxed`}>{post.content}</p>
                        
                        {/* Media Display */}
                        {post.media && post.media.length > 0 && (
                          <div className={`mt-4 grid gap-2 ${post.media.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                            {post.media.map((media, idx) => (
                              <div key={media.id} className="rounded-lg overflow-hidden bg-black/10">
                                {media.type === 'image' ? (
                                  <img 
                                    src={`${API}${media.url}`}
                                    alt="Post media"
                                    className="w-full h-auto max-h-96 object-cover"
                                  />
                                ) : (
                                  <video 
                                    src={`${API}${media.url}`}
                                    controls
                                    className="w-full h-auto max-h-96"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className={`flex items-center gap-6 mt-4 pt-4 border-t ${borderColor}`}>
                          <button 
                            onClick={() => handleLikePost(post.id)}
                            className={`flex items-center gap-2 text-sm transition-colors ${
                              post.liked_by_user ? 'text-[#C4A661]' : `${textMuted} hover:${textPrimary}`
                            }`}
                          >
                            <Heart size={18} weight={post.liked_by_user ? 'fill' : 'regular'} />
                            <span>{post.likes || 0}</span>
                          </button>
                          <button 
                            onClick={() => toggleComments(post.id)}
                            className={`flex items-center gap-2 text-sm transition-colors ${
                              expandedComments[post.id] ? 'text-[#C4A661]' : `${textMuted} hover:${textPrimary}`
                            }`}
                            data-testid={`comment-btn-${post.id}`}
                          >
                            <ChatCircle size={18} weight={expandedComments[post.id] ? 'fill' : 'regular'} />
                            <span>{post.comment_count || 0}</span>
                          </button>
                          <button 
                            onClick={() => openShareModal(post)}
                            className={`flex items-center gap-2 text-sm ${textMuted} hover:${textPrimary} transition-colors`}
                            data-testid={`share-btn-${post.id}`}
                          >
                            <ShareNetwork size={18} />
                            <span>Share</span>
                          </button>
                        </div>

                        {/* Comments Section */}
                        <AnimatePresence>
                          {expandedComments[post.id] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className={`mt-4 pt-4 border-t ${borderColor} overflow-hidden`}
                            >
                              {/* Comment Input */}
                              <div className="flex gap-3 mb-4">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C4A661] to-[#8B6914] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                <div className="flex-1">
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={newComment[post.id] || ''}
                                      onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                                      onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment(post.id)}
                                      placeholder="Write a comment..."
                                      className={`flex-1 px-3 py-2 rounded-lg text-sm ${
                                        isLightTheme 
                                          ? 'bg-[#F5F3EE] border-[#E5E2DB] text-[#1A1A1A] placeholder-[#737373]' 
                                          : 'bg-[#0A0A0A] border-[#333] text-[#F7F7F5] placeholder-[#525252]'
                                      } border focus:outline-none focus:border-[#C4A661]`}
                                      data-testid={`comment-input-${post.id}`}
                                    />
                                    <button
                                      onClick={() => handleSubmitComment(post.id)}
                                      disabled={!newComment[post.id]?.trim()}
                                      className="px-3 py-2 rounded-lg bg-[#C4A661] text-black text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#D4B671] transition-colors"
                                      data-testid={`comment-submit-${post.id}`}
                                    >
                                      <PaperPlaneTilt size={16} weight="fill" />
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* Comments List */}
                              <div className="space-y-3 max-h-64 overflow-y-auto">
                                {comments[post.id]?.length === 0 ? (
                                  <p className={`text-sm ${textMuted} text-center py-4`}>
                                    No comments yet. Be the first to comment!
                                  </p>
                                ) : (
                                  comments[post.id]?.map((comment) => (
                                    <div key={comment.id} className="flex gap-3">
                                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#525252] to-[#333] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                        {comment.author_name?.charAt(0)?.toUpperCase() || 'U'}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className={`text-sm font-semibold ${textPrimary}`}>
                                            {comment.author_name}
                                          </span>
                                          <span className={`text-xs ${textMuted}`}>
                                            {new Date(comment.created_at).toLocaleDateString()}
                                          </span>
                                        </div>
                                        <p className={`text-sm ${textSecondary}`}>{comment.content}</p>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </main>

          {/* Right Sidebar */}
          <aside className="hidden lg:block space-y-6">
            {/* Quick Links */}
            <div className={`${bgCard} border ${borderColor} p-5`}>
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#C4A661] mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link to="/getting-started" className={`flex items-center justify-between py-2 text-sm ${textSecondary} hover:${textPrimary} transition-colors`}>
                  <span>Getting Started Guide</span>
                  <CaretRight size={14} />
                </Link>
                <Link to="/courses" className={`flex items-center justify-between py-2 text-sm ${textSecondary} hover:${textPrimary} transition-colors`}>
                  <span>Training</span>
                  <CaretRight size={14} />
                </Link>
                {isPaidMember && (
                  <Link to="/tools" className={`flex items-center justify-between py-2 text-sm ${textSecondary} hover:${textPrimary} transition-colors`}>
                    <span>Ecosystem Tools</span>
                    <CaretRight size={14} />
                  </Link>
                )}
              </div>
            </div>

            {/* Your Status */}
            <div className={`${bgCard} border ${borderColor} p-5`}>
              <h3 className={`text-xs font-bold uppercase tracking-[0.15em] ${textSecondary} mb-4`}>Your Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${textSecondary}`}>Member Since</span>
                  <span className={`text-sm font-medium ${textPrimary}`}>
                    {new Date(user?.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${textSecondary}`}>Status</span>
                  <span className={`text-sm font-medium ${isPaidMember ? 'text-[#C4A661]' : textPrimary}`}>
                    {isPaidMember ? 'Inner Circle' : 'Free Member'}
                  </span>
                </div>
              </div>
            </div>

            {/* Encouragement Card */}
            <div className={`bg-gradient-to-br from-[#C4A661]/10 to-transparent border border-[#C4A661]/30 p-5`}>
              <Cross size={24} className="text-[#C4A661] mb-3" weight="duotone" />
              <p className={`text-sm ${textSecondary} leading-relaxed`}>
                "And let us consider how to stir up one another to love and good works, not neglecting to meet together..."
              </p>
              <p className="text-xs text-[#C4A661] mt-2">— Hebrews 10:24-25</p>
            </div>
          </aside>
        </div>
      </div>

      {/* New Post Modal with Media Upload */}
      <AnimatePresence>
        {showNewPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowNewPost(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#141414] border border-[#262626] p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#F7F7F5]">Share with the Brotherhood</h3>
                <button onClick={() => setShowNewPost(false)} className="text-[#A3A3A3] hover:text-[#F7F7F5]">
                  <X size={24} />
                </button>
              </div>
              
              <div className="mb-4">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#C4A661]">
                  Posting to: {navItems.find(n => n.id === activeSection)?.label || 
                               innerCircleItems.find(n => n.id === activeSection)?.label || 'Feed'}
                  {innerCircleItems.find(n => n.id === activeSection) && (
                    <Crown size={12} className="inline-block ml-1 -mt-0.5" weight="fill" />
                  )}
                </span>
              </div>

              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="What's on your mind, brother? Share a win, ask for prayer, or encourage someone..."
                className="w-full bg-[#0A0A0A] border border-[#262626] p-4 text-[#F7F7F5] placeholder-[#525252] focus:outline-none focus:border-[#C4A661] resize-none"
                rows={5}
                data-testid="post-content-input"
              />
              
              {/* Media Preview */}
              {selectedMedia.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {selectedMedia.map((media, index) => (
                    <div key={media.preview || `media-${index}`} className="relative group rounded-lg overflow-hidden bg-[#262626]">
                      {media.type === 'image' ? (
                        <img 
                          src={media.preview} 
                          alt="Preview" 
                          className="w-full h-32 object-cover"
                        />
                      ) : (
                        <video 
                          src={media.preview} 
                          className="w-full h-32 object-cover"
                        />
                      )}
                      <button
                        onClick={() => removeSelectedMedia(index)}
                        className="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded text-xs text-white">
                        {media.type === 'image' ? <ImageIcon size={12} /> : <VideoCamera size={12} />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Media Upload Buttons */}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#262626]">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={selectedMedia.length >= 4}
                  className="flex items-center gap-2 px-4 py-2 bg-[#262626] hover:bg-[#363636] text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ImageIcon size={18} />
                  <span className="text-sm">Photo</span>
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={selectedMedia.length >= 4}
                  className="flex items-center gap-2 px-4 py-2 bg-[#262626] hover:bg-[#363636] text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <VideoCamera size={18} />
                  <span className="text-sm">Video</span>
                </button>
                <span className="text-xs text-[#525252] ml-auto">
                  {selectedMedia.length}/4 media
                </span>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button 
                  onClick={() => {
                    setShowNewPost(false);
                    setSelectedMedia([]);
                    setNewPostContent('');
                  }}
                  className="btn-outline"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitPost}
                  disabled={(!newPostContent.trim() && selectedMedia.length === 0) || submitting}
                  className="btn-gold"
                  data-testid="submit-post-btn"
                >
                  {uploadingMedia ? 'Uploading...' : submitting ? 'Posting...' : 'Post'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Daily Devotional Modal */}
      <AnimatePresence>
        {showDevotional && devotional && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDevotional(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="bg-[#C4A661] px-6 py-4 flex items-center justify-between sticky top-0">
                <div className="flex items-center gap-3">
                  <BookBookmark size={24} className="text-[#0A0A0A]" weight="fill" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]/70">{devotional.date}</p>
                    <h3 className="text-lg font-bold text-[#0A0A0A]">{devotional.title}</h3>
                  </div>
                </div>
                <button
                  onClick={() => setShowDevotional(false)}
                  className="text-[#0A0A0A] hover:text-[#0A0A0A]/70 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Scripture */}
                <div className="bg-[#141414] border border-[#262626] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Cross size={16} className="text-[#C4A661]" weight="fill" />
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#C4A661]">Scripture</span>
                  </div>
                  <p className="text-xl font-editorial italic text-[#F7F7F5] mb-3">
                    "{devotional.scripture}"
                  </p>
                  <p className="text-sm text-[#C4A661] font-medium">— {devotional.reference}</p>
                </div>

                {/* Reflection */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkle size={16} className="text-[#C4A661]" weight="fill" />
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#C4A661]">Reflection</span>
                  </div>
                  <p className="text-[#A3A3A3] leading-relaxed">
                    {devotional.reflection}
                  </p>
                </div>

                {/* Today's Action */}
                <div className="bg-[#141414] border border-[#C4A661]/30 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Target size={16} className="text-[#C4A661]" weight="fill" />
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#C4A661]">Today's Action</span>
                  </div>
                  <p className="text-[#F7F7F5] font-medium">
                    {devotional.action}
                  </p>
                </div>
              </div>

              {/* Footer CTA */}
              <div className="bg-[#141414] border-t border-[#262626] px-6 py-4">
                <button
                  onClick={() => setShowDevotional(false)}
                  className="w-full btn-gold py-3 flex items-center justify-center gap-2"
                >
                  <CheckCircle size={18} weight="fill" />
                  Close & Apply Today's Action
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Welcome Modal for New Users */}
      <WelcomeModal 
        isOpen={showWelcomeModal} 
        onClose={() => setShowWelcomeModal(false)} 
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={shareData.title}
        shareUrl={shareData.url}
        shareText={shareData.text}
        shareType="post"
      />
    </div>
  );
};

export default CommunityDashboard;
